/**
 * Maximum take-off thrust (static, sea level) in pound-force (lbf) for commercial
 * turbofan model designations. Compiled from public type-certificate data sheets,
 * manufacturer / airframe specification summaries, and Wikipedia "Variants" tables
 * (CFM56, V2500, CF6, GE90, GEnx, LEAP, PW1000G/1500G/1900G, CF34, BR700, GP7200,
 * Trent 900, PW2000, RB211-535, etc.).
 *
 * Thrust ratings are certification/marketing static take-off values; operational
 * derates and temperature limits are not represented.
 */
const rawThrustRatingEntries = [
    // --- CFM56-2 (military / re-engine) ---
    ['CFM56-2A2', 24000],
    ['CFM56-2A3', 24000],
    ['CFM56-2B1', 22000],
    ['CFM56-2C1', 22000],
    // --- CFM56-3 (737 Classic) ---
    ['CFM56-3B1', 20000],
    ['CFM56-3B2', 22000],
    ['CFM56-3C1', 23500],
    // --- CFM56-5A (A320ceo early) ---
    ['CFM56-5A1', 25000],
    ['CFM56-5A3', 26500],
    ['CFM56-5A4', 22000],
    ['CFM56-5A5', 23500],
    // --- CFM56-5B (A318/A319/A320/A321) — /P, /2, /3 are combustor/software; same rated thrust ---
    ['CFM56-5B1', 30000],
    ['CFM56-5B2', 31000],
    ['CFM56-5B3', 33000],
    ['CFM56-5B4', 27000],
    ['CFM56-5B5', 22000],
    ['CFM56-5B6', 23500],
    ['CFM56-5B7', 27000],
    ['CFM56-5B8', 21600],
    ['CFM56-5B9', 23300],
    ['CFM56-5B1/P', 30000],
    ['CFM56-5B2/P', 31000],
    ['CFM56-5B3/P', 33000],
    ['CFM56-5B3/P1', 33000],
    ['CFM56-5B4/P', 27000],
    ['CFM56-5B4/P1', 27000],
    ['CFM56-5B5/P', 22000],
    ['CFM56-5B6/P', 23500],
    ['CFM56-5B7/P', 27000],
    ['CFM56-5B8/P', 21600],
    ['CFM56-5B9/P', 23300],
    ['CFM56-5B1/3', 30000],
    ['CFM56-5B2/3', 31000],
    ['CFM56-5B3/3', 33000],
    ['CFM56-5B4/3', 27000],
    ['CFM56-5B5/3', 22000],
    ['CFM56-5B6/3', 23500],
    ['CFM56-5B7/3', 27000],
    ['CFM56-5B8/3', 21600],
    ['CFM56-5B9/3', 23300],
    ['CFM56-5B3/3B1', 33000],
    ['CFM56-5B4/3B1', 27000],
    ['CFM56-5B1P', 30000],
    ['CFM56-5B2P', 31000],
    ['CFM56-5B3P', 33000],
    ['CFM56-5B4P', 27000],
    ['CFM56-5B5P', 22000],
    ['CFM56-5B6P', 23500],
    ['CFM56-5B7P', 27000],
    ['CFM56-5B8P', 21600],
    ['CFM56-5B9P', 23300],
    // --- CFM56-5C (A340-200/300) ---
    ['CFM56-5C2', 31200],
    ['CFM56-5C3', 32500],
    ['CFM56-5C4', 34000],
    // --- CFM56-7B (737NG) ---
    ['CFM56-7B18', 19500],
    ['CFM56-7B20', 20600],
    ['CFM56-7B22', 22700],
    ['CFM56-7B24', 24200],
    ['CFM56-7B26', 26300],
    ['CFM56-7B27', 27300],
    ['CFM56-7B18/2', 19500],
    ['CFM56-7B20/2', 20600],
    ['CFM56-7B22/2', 22700],
    ['CFM56-7B24/2', 24200],
    ['CFM56-7B26/2', 26300],
    ['CFM56-7B27/2', 27300],
    ['CFM56-7B18/3', 19500],
    ['CFM56-7B20/3', 20600],
    ['CFM56-7B22/3', 22700],
    ['CFM56-7B24/3', 24200],
    ['CFM56-7B26/3', 26300],
    ['CFM56-7B27/3', 27300],
    ['CFM56-7B24E', 24200],
    ['CFM56-7B26E', 26300],
    ['CFM56-7B27E', 27300],
    ['CFM56-7B27A', 27300],
    // --- LEAP-1A (A320neo family) — model number ≈ thrust class (lbf × 1000); fine-tuned to public cert summaries ---
    ['LEAP-1A21', 21000],
    ['LEAP-1A22', 22000],
    ['LEAP-1A23', 23000],
    ['LEAP-1A24', 24013],
    ['LEAP-1A26', 27100],
    ['LEAP-1A29', 29000],
    ['LEAP-1A32', 32100],
    ['LEAP-1A33', 33100],
    ['LEAP-1A35', 35100],
    // --- LEAP-1B (737 MAX) ---
    ['LEAP-1B25', 25000],
    ['LEAP-1B26', 26000],
    ['LEAP-1B27', 27000],
    ['LEAP-1B28', 28000],
    ['LEAP-1B29', 29000],
    // --- LEAP-1C (C919) ---
    ['LEAP-1C', 30830],
    // --- IAE V2500 ---
    ['V2500-A1', 24800],
    ['V2522-A5', 23040],
    ['V2524-A5', 23500],
    ['V2527-A5', 24480],
    ['V2527E-A5', 24480],
    ['V2527M-A5', 29900],
    ['V2530-A5', 29970],
    ['V2533-A5', 31600],
    ['V2525-D5', 25000],
    ['V2528-D5', 28000],
    ['V2531-E5', 31330],
    // --- PW1100G-JM (A320neo) — the original seven entries carry Wikipedia / EASA-style cert
    // rounding (unverified); the GA/G1/R marks added under DR-4085 carry take-off thrust from UK CAA
    // TCDS UK.TC.E.00127 Issue 03 and FAA TCDS E00087EN Rev 2. The "A" marks (e.g. PW1127GA-JM,
    // PW1133GA-JM) are alternate-climb-thrust variants with the same take-off thrust as their "G" twin. ---
    ['PW1121G-JM', 21000],
    ['PW1122G-JM', 24240],
    ['PW1124G-JM', 24240],
    ['PW1124G1-JM', 24240],
    ['PW1125G-JM', 25000],
    ['PW1127G-JM', 27075],
    ['PW1127GA-JM', 27075],
    ['PW1127G1-JM', 27075],
    ['PW1127G1A-JM', 27075],
    ['PW1127G1B-JM', 27075],
    ['PW1129G-JM', 29245],
    ['PW1130G-JM', 30000],
    ['PW1133G-JM', 33110],
    ['PW1133GA-JM', 33110],
    ['PW1133G1-JM', 33110],
    ['PW1133GR-JM', 33110],
    ['PW1133GAR-JM', 33110],
    ['PW1133G1R-JM', 33110],
    // --- PW1500G (Airbus A220) ---
    ['PW1519G', 18900],
    ['PW1521G', 21000],
    ['PW1524G', 24400],
    ['PW1525G', 24400],
    // --- PW1900G (Embraer E-Jets E2) ---
    ['PW1919G', 19000],
    ['PW1921G', 21000],
    ['PW1922G', 22000],
    ['PW1923G', 23000],
    // --- CF34 (regional / business) ---
    ['CF34-3A', 9220],
    ['CF34-3B', 9220],
    ['CF34-8C5', 13790],
    ['CF34-8C5A1', 14500],
    // CF34-8E per-configuration values from MTU `CF34-8E_GenericInput_V7.xlsx`
    // (`Engine_Configs` sheet, `Max Thrust`). They replace the single public
    // 14500 figure the three base marks carried before, so the family is now on
    // one scale; the workbook's SHA-256 is in `llpCatalog/cf34-8e.ts`.
    ['CF34-8E2', 12410],
    ['CF34-8E2A1', 13300],
    ['CF34-8E5', 13420],
    ['CF34-8E5A1', 14050],
    ['CF34-8E5A2', 14510],
    ['CF34-8E5A2HA', 14510],
    ['CF34-8E6', 13420],
    ['CF34-8E6A1', 14050],
    ['CF34-10A', 17640],
    ['CF34-10A16', 17640],
    ['CF34-10E', 20360],
    ['CF34-10E5', 20360],
    ['CF34-10E5A1', 20360],
    ['CF34-10E6', 20360],
    ['CF34-10E7', 20360],
    // --- Rolls-Royce BR700 (Boeing 717) ---
    ['BR700-715A1-30', 18500],
    ['BR700-715C1-30', 21000],
    ['BR715A1-30', 18500],
    ['BR715C1-30', 21000],
    // --- CF6 (widebody) — CF6-80C2 sub-models (GE / TCDS summaries) ---
    ['CF6-80C2-B1F', 56700],
    ['CF6-80C2-B2F', 57900],
    ['CF6-80C2-B4F', 58000],
    ['CF6-80C2-B5F', 59700],
    ['CF6-80C2-B6F', 60800],
    ['CF6-80C2-B7F', 61500],
    ['CF6-80C2-B8F', 62000],
    ['CF6-80C2-B1F1', 56700],
    ['CF6-80C2-D1F', 62000],
    ['CF6-80A2', 48000],
    ['CF6-80E1A2', 65500],
    ['CF6-80E1A3', 67500],
    ['CF6-80E1A4', 72000],
    ['CF6-6D', 41500],
    ['CF6-50C2', 52000],
    ['CF6-50E2', 54000],
    // --- GEnx (take-off thrust; EASA TCDS IM.E.102 Issue 12) ---
    ['GEnx-1B54', 57400],
    ['GEnx-1B58', 61000],
    ['GEnx-1B64', 67000],
    ['GEnx-1B67', 69400],
    ['GEnx-1B70', 72300],
    ['GEnx-1B70/75', 72300],
    ['GEnx-1B74/75', 76700],
    ['GEnx-1B75', 77600],
    ['GEnx-1B76', 78500],
    ['GEnx-1B78', 80400],
    ['GEnx-1B54/P1', 57400],
    ['GEnx-1B58/P1', 61000],
    ['GEnx-1B64/P1', 67000],
    ['GEnx-1B67/P1', 69400],
    ['GEnx-1B70/P1', 72300],
    ['GEnx-1B70/75/P1', 72300],
    ['GEnx-1B74/75/P1', 76700],
    ['GEnx-1B75/P1', 77600],
    ['GEnx-1B76/P1', 78500],
    ['GEnx-1B78/P1', 80400],
    ['GEnx-1B54/P2', 57400],
    ['GEnx-1B58/P2', 61000],
    ['GEnx-1B64/P2', 67000],
    ['GEnx-1B67/P2', 69400],
    ['GEnx-1B70/P2', 72300],
    ['GEnx-1B70/75/P2', 72300],
    ['GEnx-1B74/75/P2', 76700],
    ['GEnx-1B75/P2', 77600],
    ['GEnx-1B76/P2', 78500],
    ['GEnx-1B78/P2', 80400],
    ['GEnx-2B67', 67400],
    ['GEnx-2B67B', 67400],
    ['GENX-1B54', 57400],
    ['GENX-1B58', 61000],
    ['GENX-1B64', 67000],
    ['GENX-1B67', 69400],
    ['GENX-1B70', 72300],
    ['GENX-1B75', 77600],
    ['GENX-1B78', 80400],
    ['GENX-2B67', 67400],
    // --- GE90 (777) ---
    ['GE90-76B', 81070],
    ['GE90-77B', 77000],
    ['GE90-85B', 85000],
    ['GE90-90B', 90000],
    ['GE90-94B', 93700],
    ['GE90-110B1', 110760],
    ['GE90-113B', 113500],
    ['GE90-115B', 115300],
    // --- Engine Alliance GP7200 (A380) ---
    ['GP7270', 70000],
    ['GP7277', 77250],
    ['GP7200', 70000],
    // --- Rolls-Royce Trent 500 (A340-500/600, EASA TCDS E.060) ---
    ['Trent553-61', 55775],
    ['Trent553A2-61', 55775],
    ['Trent556-61', 58450],
    ['Trent556A2-61', 58450],
    ['Trent560A2-61', 61890],
    // --- Trent 700 (A330, typical cert max ~71.1k lbf class) ---
    ['Trent768', 71100],
    ['Trent772', 71100],
    ['Trent772B', 71100],
    ['Trent772C', 71100],
    // Certified RB211 Trent 700 marks. Net take-off (5 min), sea-level static ISA
    // from EASA TCDS E.042 Issue 07, 13 October 2022, §III.6 Rating
    // (https://www.easa.europa.eu/hr/downloads/7723/en): 768-60 = 300.3 kN
    // (67500 lbf); 772-60 / 772B-60 / 772C-60 = 316.3 kN (71100 lbf). 772B-60/16
    // is not a TCDS model — it is a Rolls-Royce build standard on 772B-60 with
    // the same sea-level take-off thrust. Kept as its own key because the LLP
    // catalog already lists it as a distinct variant (llpCatalog/trent-700.ts).
    ['Trent 768-60', 67500],
    ['Trent 772-60', 71100],
    ['Trent 772B-60', 71100],
    ['Trent 772C-60', 71100],
    ['Trent 772B-60/16', 71100],
    // --- Trent 800 (777 Classic, EASA variants) ---
    ['Trent875-17', 76580],
    ['Trent877-17', 78910],
    ['Trent884-17', 85430],
    ['Trent884B-17', 85430],
    ['Trent892-17', 91450],
    ['Trent892B-17', 91450],
    ['Trent895-17', 92940],
    ['Trent875', 76580],
    ['Trent877', 78910],
    ['Trent884', 85430],
    ['Trent892', 91450],
    ['Trent895', 92940],
    // --- Trent 900 (A380) ---
    ['Trent970-84', 72000],
    ['Trent970B-84', 72000],
    ['Trent972-84', 74740],
    ['Trent972B-84', 74740],
    ['Trent977-84', 76500],
    ['Trent977B-84', 76500],
    ['Trent980-84', 80000],
    // --- Trent 1000 (787, EASA take-off ratings by package — Wikipedia) ---
    ['Trent1000-A', 69194],
    ['Trent1000-A2', 69194],
    ['Trent1000-AE', 69194],
    ['Trent1000-AE2', 69194],
    ['Trent1000-AE3', 69194],
    ['Trent1000-C', 74511],
    ['Trent1000-C2', 74511],
    ['Trent1000-CE', 74511],
    ['Trent1000-CE2', 74511],
    ['Trent1000-CE3', 74511],
    ['Trent1000-D', 74511],
    ['Trent1000-D2', 74511],
    ['Trent1000-D3', 74511],
    ['Trent1000-E', 62264],
    ['Trent1000-E2', 62264],
    ['Trent1000-G', 72066],
    ['Trent1000-G2', 72066],
    ['Trent1000-G3', 72066],
    ['Trent1000-H', 63897],
    ['Trent1000-H2', 63897],
    ['Trent1000-H3', 63897],
    ['Trent1000-J2', 78129],
    ['Trent1000-J3', 78129],
    ['Trent1000-K2', 78129],
    ['Trent1000-K3', 78129],
    ['Trent1000-L2', 74551],
    ['Trent1000-L3', 74551],
    ['Trent1000-M3', 79728],
    ['Trent1000-N3', 79728],
    ['Trent1000-P3', 74551],
    ['Trent1000-Q3', 78129],
    ['Trent1000-R3', 81028],
    ['Trent7000', 72000],
    ['Trent7000-72', 72000],
    ['TrentXWB-75', 75000],
    ['TrentXWB-84', 84000],
    ['TrentXWB-97', 97000],
    // --- RB211-524 (747 / 767) ---
    ['RB211-524B', 50000],
    ['RB211-524C2', 53000],
    ['RB211-524D4', 58000],
    ['RB211-524G', 58000],
    ['RB211-524H', 60600],
    // --- RB211-535 (757) ---
    ['RB211-535C', 37400],
    ['RB211-535E4', 40100],
    ['RB211-535E4B', 43100],
    ['RB211-535E4-37', 40100],
    ['RB211-535E4B-37', 43100],
    // --- PW2000 (757) ---
    ['PW2037', 36600],
    ['PW2040', 40100],
    ['PW2043', 42600],
    // --- PW4000 (777 / 747 / A330) — selected thrust ratings ---
    ['PW4052', 52000],
    ['PW4056', 56750],
    ['PW4060', 60000],
    ['PW4062', 62300],
    ['PW4062A', 62000],
    // 100-inch fan (777 classic)
    ['PW4074', 74000],
    ['PW4074D', 74000],
    ['PW4077', 77000],
    ['PW4077D', 77000],
    ['PW4084', 84000],
    ['PW4084D', 84000],
    ['PW4090', 90000],
    ['PW4098', 98000],
    ['PW4152', 52000],
    ['PW4158', 58000],
    ['PW4164', 64000],
    ['PW4168', 68400],
    ['PW4170', 70000],
    // --- JT8D / JT9D (legacy) ---
    ['JT8D-7', 14000],
    ['JT8D-9', 14500],
    ['JT8D-15', 15500],
    ['JT8D-17', 16000],
    ['JT8D-17R', 16000],
    ['JT8D-217', 21000],
    ['JT8D-219', 21000],
    ['JT9D-7R4D', 48000],
    ['JT9D-7R4E', 50500],
    ['JT9D-7R4E4', 56000],
    ['JT9D-7R4G2', 58000],
    ['JT9D-7Q3', 52000],
    // --- GE9X (777X) ---
    ['GE9X-105B1A', 105000],
];
function canonicalThrustRatingKey(key) {
    return key.replace(/\s+/g, '').toUpperCase();
}
export const thrustRatingMap = new Map(rawThrustRatingEntries.map(([key, value]) => [canonicalThrustRatingKey(key), value]));
/**
 * Looks up rated take-off thrust (lbf) after normalising spaces and case.
 * Optional `suffixStrip` removes a trailing CFM engine dash number such as `-G05` and a
 * trailing combustor/program suffix such as `/3` or `/P1` once, so e.g. `CFM56-5B4/3` and
 * `CFM56-7B27E-G05` can match `CFM56-5B4` / `CFM56-7B27E`.
 */
export function lookupThrustRatingLbf(raw, options) {
    if (raw == null || raw === '') {
        return undefined;
    }
    let key = canonicalThrustRatingKey(raw);
    const direct = thrustRatingMap.get(key);
    if (direct !== undefined) {
        return direct;
    }
    if (options?.suffixStrip) {
        key = key
            .replace(/-G\d{2,3}$/i, '')
            .replace(/\/P\d+$/i, '')
            .replace(/\/[23](B1)?$/i, '');
        return thrustRatingMap.get(key);
    }
    return undefined;
}
/**
 * Separator-insensitive form used only for matching: strips every non-alphanumeric
 * character (hyphens, slashes, spaces) and uppercases, so `CF6-80C2 B1F`,
 * `CF6-80C2B1F` and `CF6-80C2-B1F` all reduce to the same token.
 */
function looseThrustRatingKey(key) {
    return key.replace(/[^a-z0-9]/gi, '').toUpperCase();
}
/**
 * Loose token -> canonical `thrustRatingMap` key, for the map keys themselves.
 * Collapsing separators collides only pairs that differ by a `/` (e.g. `CFM56-5B4/P`
 * vs `CFM56-5B4P`), and every such pair carries an identical rating — so the tiebreak
 * (lexicographically smallest original designation, code-point order, insertion-order
 * independent) never changes the thrust a caller ends up with.
 */
const canonicalLooseThrustRatingIndex = new Map(Array.from(rawThrustRatingEntries
    .map(([key]) => key)
    .reduce((winners, key) => {
    const loose = looseThrustRatingKey(key);
    const incumbent = winners.get(loose);
    return incumbent !== undefined && incumbent < key ? winners : winners.set(loose, key);
}, new Map()), ([loose, key]) => [loose, canonicalThrustRatingKey(key)]));
/**
 * Printed Trent 700 forms that are not themselves map keys. Each maps onto one
 * certified mark after `looseThrustRatingKey` (RB211 prefix, missing "Trent",
 * glued `/16`). `TRENT 772B-60/16` is listed even though it already matches the
 * canonical key, so the high-count drop-log spellings stay in one place.
 * Rolls-Royce EASA Form 1s for a 772C print the same `/16` build standard
 * (`RB211 TRENT 772C-60/16`; DR-4364). The catalog carries no 772C-60/16 variant,
 * unlike 772B, so those forms fold onto the certified `Trent 772C-60` mark.
 */
const TRENT_700_PRINTED_ALIASES = [
    ['RB211 TRENT 772B-60/16', 'Trent 772B-60/16'],
    ['TRENT 772B-60/16', 'Trent 772B-60/16'],
    ['772B-60/16', 'Trent 772B-60/16'],
    ['772B-60', 'Trent 772B-60'],
    ['RB211 TRENT 772B-60', 'Trent 772B-60'],
    ['RB211 TRENT 772C-60/16', 'Trent 772C-60'],
    ['TRENT 772C-60/16', 'Trent 772C-60'],
    ['772C-60/16', 'Trent 772C-60'],
    ['772C-60', 'Trent 772C-60'],
    ['RB211 TRENT 772C-60', 'Trent 772C-60'],
];
/**
 * The canonical index plus printed-form aliases. `-JM` entries also accept the
 * designation without the trailing application suffix (`PW1133GA` ->
 * `PW1133GA-JM`; DR-4085). Every key also accepts a trailing `SERIES` (DR-4364).
 * Trent 700 pages print `RB211 TRENT 772B-60/16` and `772B-60/16`; those resolve to
 * the certified `-60` keys.
 *
 * Aliases are added only after the whole canonical set exists, so a clash is detected
 * regardless of entry order. An alias that would shadow a different entry is a data
 * error and throws at module load. No suffix-strip or regex engine: each alias is a
 * data pair.
 */
const looseThrustRatingIndexWithJm = rawThrustRatingEntries
    .map(([key]) => key)
    .filter((key) => /-JM$/i.test(key))
    .reduce((index, key) => {
    const alias = looseThrustRatingKey(key.replace(/-JM$/i, ''));
    const canonical = canonicalThrustRatingKey(key);
    const incumbent = index.get(alias);
    if (incumbent !== undefined && incumbent !== canonical) {
        throw new Error(`thrustRatingMap: the bare alias "${alias}" of "${key}" already resolves to "${incumbent}"`);
    }
    return index.set(alias, canonical);
}, new Map(canonicalLooseThrustRatingIndex));
/**
 * `<designation> SERIES` aliases (DR-4364). GE/CFM EDS "Engine Type" headers print
 * the rating as `CFM56-7 --B26 SERIES`; the drop log shows only separator spellings
 * of that shape (`CFM56-7 -B26 SERIES`, `CFM56-7-B22 SERIES`, `CFM56-5 --B3/P SERIES`).
 * The loose key collapses separators, so one alias per map key covers them all. The
 * alias targets the canonical-index winner, so the `/P` vs `P` pair keeps its
 * deterministic canonical. Bare forms (`B26 SERIES`) are excluded: never observed,
 * and ambiguous across the CFM56-2/3/5 B1/B2 marks.
 */
const looseThrustRatingIndexWithSeries = rawThrustRatingEntries
    .map(([key]) => key)
    .reduce((index, key) => {
    const alias = `${looseThrustRatingKey(key)}SERIES`;
    const canonical = canonicalLooseThrustRatingIndex.get(looseThrustRatingKey(key)) ??
        canonicalThrustRatingKey(key);
    const incumbent = index.get(alias);
    if (incumbent !== undefined && incumbent !== canonical) {
        throw new Error(`thrustRatingMap: the SERIES alias "${alias}" of "${key}" already resolves to "${incumbent}"`);
    }
    return index.set(alias, canonical);
}, looseThrustRatingIndexWithJm);
const looseThrustRatingIndex = TRENT_700_PRINTED_ALIASES.reduce((index, [printed, key]) => {
    const alias = looseThrustRatingKey(printed);
    const canonical = canonicalThrustRatingKey(key);
    const incumbent = index.get(alias);
    if (incumbent !== undefined && incumbent !== canonical) {
        throw new Error(`thrustRatingMap: the printed alias "${printed}" already resolves to "${incumbent}"`);
    }
    return index.set(alias, canonical);
}, looseThrustRatingIndexWithSeries);
/**
 * Resolves free-text thrust wording to the canonical engine variant designation —
 * always a real key of {@link thrustRatingMap} (e.g. `CF6-80C2 B1F` -> `CF6-80C2-B1F`).
 * Matching ignores separators and case.
 *
 * The `-JM` application suffix is optional on input: the bare printed mark resolves to
 * the `-JM` canonical key (`PW1133GA` -> `PW1133GA-JM`), while `G`, `GA`, `G1`, `GR`,
 * `GAR` and `G1R` remain distinct marks. Trent 700 printed forms with an `RB211`
 * prefix or a `/16` build standard resolve to the certified `-60` keys. A trailing
 * `SERIES` (`CFM56-7 --B26 SERIES`) is accepted on every key (DR-4364).
 *
 * Returns `undefined` for anything that is not a known variant: descriptive phrases
 * ("High Thrust"), bare suffixes ("B1F") and family-only designations ("CF6-80").
 * Unmapped engine families intentionally resolve to `undefined` rather than guessing —
 * callers are expected to drop the value rather than store an unverifiable one.
 */
export function resolveThrustRatingVariant(raw) {
    if (raw == null || raw === '') {
        return undefined;
    }
    return looseThrustRatingIndex.get(looseThrustRatingKey(raw));
}
/**
 * Canonical designation for a rating printed WITHOUT its family prefix — a task card writes
 * "CFM56-7B CONVERSION TO -7B22" and the after-rating is copied as "-7B22". Resolves when the
 * loose key of `raw` is a strict suffix of EXACTLY ONE canonical loose key in the catalogue
 * (the same uniqueness bar as the separator collapse above). `undefined` when `raw` already
 * resolves as a full designation (callers use `resolveThrustRatingVariant` first), when the
 * suffix is ambiguous ("3B1" is CFM56-3B1, CFM56-5B3/3B1 and CFM56-5B4/3B1), or when nothing
 * ends with it.
 *
 * The suffix must be at least three characters, START WITH A DIGIT and carry a letter. So a bare
 * figure ("24"), a letter ("B") or a two-character mark ("B3") never matches, and neither does
 * a letter-led sub-mark ("B1F", "A21", "R4D"): those are the bare suffixes DR-2217 rules are
 * dropped rather than guessed, since the family they belong to is not on the page. A digit-led
 * form ("7B22") carries the family's model digit, which is what a task card's "-7B22" prints.
 */
export function resolveThrustRatingSuffix(raw) {
    if (raw == null || raw === '') {
        return undefined;
    }
    const suffix = looseThrustRatingKey(raw);
    if (suffix.length < 3 || !/^\d/.test(suffix) || !/[A-Z]/.test(suffix)) {
        return undefined;
    }
    const matches = [
        ...new Set(Array.from(canonicalLooseThrustRatingIndex)
            .filter(([loose]) => loose.length > suffix.length && loose.endsWith(suffix))
            .map(([, canonical]) => canonical)),
    ];
    return matches.length === 1 ? matches[0] : undefined;
}
//# sourceMappingURL=thrustRatingMap.js.map